import { makeEntityId } from '@web-platform/shared-universal-types-utils/src/utils';

import type {
  RadarItinerary,
  RadarLeg,
  RadarSegment,
} from '@web-platform/shared-universal-types/src/network/endpoints/radar/radarItineraryTypes';
import type { Leg } from '@web-platform/shared-universal-types/src/redux/state/entities/flights/legs';
import type { EntityId } from '@web-platform/shared-universal-types-utils/src/utils';

const getUniqueCarrierIds = (carrierIds: number[]) => Array.from(new Set(carrierIds));

const getStopIds = (segments: RadarSegment[]): EntityId[][] => {
  if (segments.length < 2) {
    return [];
  }

  const segmentPairs: RadarSegment[][] = segments.reduce<RadarSegment[][]>(
    (pairs, _, index, segmentArray) =>
      index === 0 ? pairs : [...pairs, [segmentArray[index - 1], segmentArray[index]]],
    [],
  );

  return segmentPairs.reduce((ids: EntityId[][], [segment1, segment2]) => {
    if (
      segment1.destination.entityId !== segment2.origin.entityId &&
      segment1.destination.parent.entityId === segment2.origin.parent.entityId
    ) {
      return [
        ...ids,
        [makeEntityId(segment1.destination.entityId), makeEntityId(segment2.origin.entityId)],
      ];
    }

    return [...ids, [makeEntityId(segment1.destination.entityId)]];
  }, []);
};

export const mapRadarLegs = (itineraries: RadarItinerary[]): Leg[] => {
  const legIdsMap = new Map();
  const legs: Leg[] = [];
  itineraries.forEach((itinerary: RadarItinerary) =>
    itinerary.legs.forEach((leg: RadarLeg) => {
      if (!legIdsMap.get(leg.id)) {
        legs.push({
          id: leg.id,
          origin_place_id: leg.origin.id,
          destination_place_id: leg.destination.id,
          origin_entity_id: makeEntityId(leg.origin.entityId),
          destination_entity_id: makeEntityId(leg.destination.entityId),
          departure: leg.departure,
          arrival: leg.arrival,
          segment_ids: leg.segments.map((segment: RadarSegment) => segment.id),
          duration: leg.durationInMinutes,
          stop_count: leg.stopCount,
          marketing_carrier_ids: getUniqueCarrierIds(
            leg.segments.map((segment: RadarSegment) => segment.marketingCarrier.id),
          ),
          operating_carrier_ids: getUniqueCarrierIds(
            leg.segments.map((segment: RadarSegment) => segment.operatingCarrier.id),
          ),
          stop_ids: getStopIds(leg.segments),
        });
        legIdsMap.set(leg.id, true);
      }
    }),
  );
  return legs;
};
